'use client';

import React from 'react';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { LiveKitRoom } from '@livekit/components-react';
import '@livekit/components-styles';
import { useTranslation } from 'next-i18next';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { useSwrStartInterview } from '@hooks/useSwrApi/endpoints/interview/useSwrStartInterview';
import { Button } from '@components/common';
import AudioInterviewRoomContent from './AudioInterviewRoomContent';
import ElevenLabsInterviewRoom from './providers/elevenlabs/ElevenLabsInterviewRoom';

interface AudioInterviewRoomProps {
  locale: Locale;
  slug?: string;
  onDisconnect: () => void;
}

/**
 * LiveKitInterviewRoom
 *
 * Internal component for LiveKit-based interview room.
 * Handles fetching interview data via SWR and connecting to LiveKit.
 */
const LiveKitInterviewRoom: React.FC<AudioInterviewRoomProps> = ({
  locale,
  slug,
  onDisconnect,
}) => {
  const { t } = useTranslation('common');

  // SWR hook automatically fetches data when this component mounts
  const {
    data: interviewData,
    isValidating,
  } = useSwrStartInterview({
    locale,
    slug,
    errorCallback: () => {
      onDisconnect();
    },
  });

  // Show loading spinner while fetching
  if (isValidating) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <TailwindSpinner className="size-8" />
      </div>
    );
  }

  // If interviewData is missing after isValidating, show an error message
  if (!interviewData || !interviewData.token || !interviewData.livekit_url) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <p className="text-center text-sm text-gray-500">{t('global.something.went.wrong')}</p>
        <Button
          color="primary"
          onClick={onDisconnect}
        >
          {t('global.action.back')}
        </Button>
      </div>
    );
  }

  // Render the actual interview room with LiveKit
  return (
    <LiveKitRoom
      token={interviewData.token}
      serverUrl={interviewData.livekit_url}
      audio
      video={false}
      connect
      className="w-full"
    >
      <AudioInterviewRoomContent onDisconnect={onDisconnect} />
    </LiveKitRoom>
  );
};

/**
 * AudioInterviewRoom
 *
 * This component handles the complete audio interview experience.
 * Supports multiple providers (LiveKit, ElevenLabs) via configuration.
 *
 * For LiveKit:
 * - Fetches interview data using SWR when component mounts
 * - Connects to LiveKit room using fetched token and server URL
 *
 * For ElevenLabs:
 * - Delegates to ElevenLabsInterviewRoom which handles token fetching
 *
 * Displays current connection state, animated Bestie avatar, and disconnect button.
 *
 * Props:
 * - locale: Language/locale for the interview
 * - slug: Optional job slug for job-based interview
 * - onDisconnect: Callback when disconnecting from the interview room
 */

const AudioInterviewRoom: React.FC<AudioInterviewRoomProps> = ({
  locale,
  slug,
  onDisconnect,
}) => {
  const headerInfo = useAppSelector(getUserHeaderInfo);

  // Provider selection rule:
  // - If a job slug exists OR user is not authenticated -> use LiveKit (legacy provider)
  // - Otherwise -> use ElevenLabs (new provider)
  const isAuthenticated = Boolean(headerInfo?.userId);
  if (!slug && isAuthenticated) {
    return (
      <ElevenLabsInterviewRoom
        locale={locale}
        onDisconnect={onDisconnect}
      />
    );
  }

  // Default: LiveKit provider
  return (
    <LiveKitInterviewRoom
      locale={locale}
      slug={slug}
      onDisconnect={onDisconnect}
    />
  );
};

export default AudioInterviewRoom;

